import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Document, Image, PDFDownloadLink, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { useEffect, useState } from 'react';

const styles = StyleSheet.create({
  page: {
    padding: 10,
    fontSize: 10,
    width: '378px',
    margin: '0 auto'
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8
  },
  logoContainer: {
    alignItems: 'center',
    padding: '16px 0',
    margin: '8px 0'
  },
  logo: {
    width: '256px',
    height: '96px',
    objectFit: 'contain'
  },
  customerInfo: {
    marginBottom: 12,
    lineHeight: 1.4
  },
  table: {
    width: '100%',
    marginTop: 8,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderStyle: 'solid',
    borderRadius: 4
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    padding: '4px 8px'
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    padding: '4px 8px'
  },
  tableCell: {
    flex: 1,
    padding: '2px 4px'
  },
  totalItems: {
    textAlign: 'right',
    marginTop: 8
  },
  light: {
    fontWeight: 300
  },
  courierName: {
    textTransform: 'uppercase'
  }
});

const setAddress = (address) => {
  if (!address) return '';
  const { city_name, desc, district_name, state_name, sub_district_name, zipcode } = address;
  return `${desc || ''}${sub_district_name ? `, kel: ${sub_district_name}` : ''}${district_name ? `, kec: ${district_name}` : ''}${city_name ? `, ${city_name}` : ''}${state_name ? `, ${state_name}` : ''}${zipcode ? `, ${zipcode}` : ''}`;
};

const getCourierName = (code) => {
  const courierMapping = {
    JAGOPACK: "LION - JAGOPACK",
    REGPACK: "LION - REGPACK",
    BIGPACK: "LION - BIGPACK",
    BOSSPACK: "LION - BOSSPACK",
    CTC: "JNE City Courier",
    JTR: "JNE Trucking",
    CTCYES: "JNE City Courier YES",
    EZ: "J&T Express"
  };
  return courierMapping[code] || code;
};

const PDFGeneratorComponent = ({ data }) => {
  const [pdfDocument, setPdfDocument] = useState(null);

  useEffect(() => {
    const generatePdf = () => {
      const MyDocument = (
        <Document>
          {data.map((object, index) => (
            <Page key={index} style={styles.page} size={[378, 595]}>
              {/* Document Header */}
              <View style={styles.header}>
                <Text>{object?.code}</Text>
                <Text>{object?.order_code || object?.SalesOrder?.order_code}</Text>
              </View>

              <View style={styles.header}>
                <Text style={styles.courierName}>
                  {getCourierName(object?.courier_package || object?.SalesOrder?.courier_package)}
                </Text>
                <Text style={styles.light}>{`by ${object?.MUser?.email}` || ''}</Text>
              </View>

              {/* Logo */}
              <View style={styles.logoContainer}>
                <Image style={styles.logo} src="/client-logo.png" />
              </View>

              {/* Customer Info */}
              <View style={styles.customerInfo}>
                <Text>
                  To: {object?.MCustomer?.firstName} {object?.MCustomer?.middleName} {object?.MCustomer?.lastName}
                </Text>
                <Text>{setAddress(object?.Address || object?.SalesOrder?.Address)}</Text>
                <Text>{object?.MCustomer?.phone || object?.SalesOrder?.MCustomer?.phone}</Text>
                {object?.note_order && (
                  <Text style={{ color: '#6b7280', fontSize: 8, marginTop: 2 }}>
                    Catatan: {object?.note_order}
                  </Text>
                )}
                {object?.SalesOrder?.note_order && (
                  <Text style={{ color: '#6b7280', fontSize: 8, marginTop: 2 }}>
                    Catatan: {object?.SalesOrder?.note_order}
                  </Text>
                )}
              </View>


              {/* Products Table */}
              <View style={styles.table}>
                <View style={styles.tableHeader}>
                  <Text style={[styles.tableCell, { flex: 2 }]}>Product Name</Text>
                  <Text style={styles.tableCell}>Code</Text>
                  <Text style={styles.tableCell}>Size</Text>
                  <Text style={styles.tableCell}>Color</Text>
                  <Text style={styles.tableCell}>Qty</Text>
                </View>

                {object?.OrderItems?.map((item, idx) => (
                  <View key={idx} style={styles.tableRow}>
                    <Text style={[styles.tableCell, { flex: 1 }]}>{item?.master_product?.name || 'N/A'}
                      {item.notes && (
                        <Text style={{ color: '#6b7280', fontSize: 8, marginTop: 2 }}>
                          Catatan: {item.notes}
                        </Text>
                      )}
                    </Text>
                    <Text style={styles.tableCell}>{item?.master_product?.code || '-'}</Text>
                    <Text style={styles.tableCell}>{item?.master_size?.name || '-'}</Text>
                    <Text style={styles.tableCell}>{item?.master_color?.name || '-'}</Text>
                    <Text style={styles.tableCell}>{item.qty || 0}</Text>
                  </View>
                ))}
              </View>

              {/* Total Items */}
              <View style={styles.totalItems}>
                <Text>
                  <Text>Total Items: </Text>
                  {object?.OrderItems?.reduce((sum, item) => sum + (item.qty || 0), 0)} pcs
                </Text>
              </View>
            </Page>
          ))}
        </Document>
      );
      setPdfDocument(MyDocument);
    };

    generatePdf();
  }, [data]);

  return (
    <div className='flex justify-end md:justify-start'>
      {data && data.length > 0 ? (pdfDocument ? (
        <PDFDownloadLink document={pdfDocument} fileName="DeliverySticker.pdf" className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
          {({ blob, url, loading, error }) => (
            <>
              <ArrowDownTrayIcon className="h-6 w-6" />
              {loading ? 'Membuat PDF...' : 'Unduh Sticker'}
            </>
          )}
        </PDFDownloadLink>
      ) : (
        <div>Loading PDF...</div>
      )) : (
        <button className="flex items-center gap-2 text-sm text-gray-400 cursor-not-allowed"
          disabled
          title="Pilih setidaknya satu item untuk diunduh"><ArrowDownTrayIcon className="h-6 w-6" /> Unduh Sticker</button>
      )}
    </div>
  );
};

export default PDFGeneratorComponent;
